// Comprehensive Wyoming family law data based on Custody X Change and official Wyoming sources
const wyomingData = {
  wyoming: {
    name: 'Wyoming',
    overview: {
      keyPoints: [
        'Wyoming follows the "best interests of the child" standard with specific factors outlined in Wyoming Statutes § 20-2-201',
        'Joint custody is not presumed - courts make individualized determinations based on evidence and circumstances',
        'Wyoming uses traditional "custody" and "visitation" terminology rather than newer parenting time language',
        'The state requires detailed parenting plans in all custody cases involving minor children',
        'Wyoming has a strong preference for maintaining meaningful relationships between children and both parents',
        'Relocation with children requires court approval or written consent from the other parent',
        'Wyoming courts emphasize stability and continuity in the child\'s living arrangements'
      ],
      uniqueFeatures: [
        'Wyoming is one of the least populous states, creating unique challenges for family court resources',
        'The state has limited family court locations, requiring travel for many rural families',
        'Wyoming allows for informal custody modifications by agreement without court approval in some cases',
        'The state recognizes common law marriage, which can affect custody and support determinations',
        'Wyoming has specific provisions for military families due to significant military presence',
        'Rural distances and weather conditions are factored into visitation schedules',
        'The state allows grandparent visitation rights under specific statutory circumstances'
      ]
    },
    legalRequirements: {
      residency: 'Either parent or the child must have lived in Wyoming for at least 6 months before filing, or Wyoming must be the child\'s home state under UCCJEA',
      filing: 'File in the county where the child has lived for the past 6 months, or where either parent resides',
      paternity: 'Unmarried fathers can establish paternity through voluntary acknowledgment, genetic testing, court order, or presumption of paternity',
      documentation: 'Required documents include petition, summons, financial affidavit, and proposed parenting plan in all cases involving children',
      uniqueRules: [
        'Wyoming requires financial disclosure in all family law cases involving children',
        'Mediation is encouraged but not mandatory in most custody disputes',
        'Guardian ad litem appointment is discretionary based on case circumstances',
        'Parent education programs may be required in some counties',
        'Wyoming allows temporary orders for emergency situations involving child welfare'
      ]
    },
    custodyTypes: {
      physicalCustody: 'Wyoming uses "physical custody" to determine where the child primarily resides and who provides day-to-day care',
      legalCustody: 'Legal custody involves decision-making authority for major life decisions including education, healthcare, and religious upbringing',
      jointCustody: 'Joint custody (both legal and physical) requires court approval and demonstration that it serves the child\'s best interests',
      visitation: 'Called "visitation rights" - includes specific schedules for the non-custodial parent\'s time with the child',
      specialProvisions: [
        'Supervised visitation may be ordered when child safety or welfare concerns exist',
        'Long-distance visitation arrangements are common due to Wyoming\'s rural nature',
        'Extended summer visitation is standard to accommodate school schedules',
        'Holiday visitation typically alternates between parents on annual basis',
        'Virtual visitation (video calls) is increasingly recognized for long-distance situations',
        'Grandparent visitation rights are protected under Wyoming Statute § 20-7-101'
      ]
    },
    parentingPlans: {
      required: true,
      elements: [
        'Detailed custody and visitation schedule including regular rotation, weekends, and holidays',
        'Specific holiday and vacation schedules with clear alternating arrangements',
        'Transportation arrangements and responsibility for travel costs between parents',
        'Communication methods between parents and guidelines for parent-child contact',
        'Decision-making authority allocation for education, healthcare, religious, and extracurricular matters',
        'Dispute resolution procedures including mediation requirements before court intervention',
        'Relocation provisions and notification requirements for moves affecting the child',
        'Any special conditions, restrictions, or accommodations for either parent',
        'Provisions for child care during work hours and right of first refusal',
        'Guidelines for introducing new romantic partners to the children'
      ],
      tips: [
        'Be extremely specific about pickup and drop-off times, locations, and procedures',
        'Include detailed provisions for Wyoming\'s challenging winter weather conditions',
        'Address how to handle missed visitation due to weather, illness, or emergencies',
        'Include specific language about travel distances and meeting locations for exchanges',
        'Specify how major decisions will be made and communicated between parents',
        'Address the child\'s participation in extracurricular activities and school events',
        'Include technology use guidelines and virtual visitation provisions for long-distance situations',
        'Plan for extended summer arrangements to maximize time with non-custodial parent'
      ]
    },
    childSupport: {
      guidelines: 'Wyoming uses a modified Income Shares Model based on both parents\' gross income and specific Wyoming factors',
      factors: [
        'Gross monthly income of both parents from all sources',
        'Number of children covered by the support order',
        'Cost of health insurance premiums for the children',
        'Childcare expenses necessary for employment or education',
        'Extraordinary medical expenses not covered by insurance',
        'Other court-ordered support obligations',
        'Visitation time and overnight stays with paying parent',
        'Standard of living the child would have enjoyed if parents remained together',
        'Special needs of the child requiring additional financial support'
      ],
      calculation: 'Wyoming Child Support Guidelines using Income Shares Model with state-specific adjustments',
      modifications: 'Substantial change in circumstances required, typically 20% change in support amount or significant change in income or circumstances',
      enforcement: 'Strong enforcement through Wyoming Department of Family Services including wage garnishment, asset seizure, tax intercept, and license suspension',
      detailedInfo: {
        basicCalculation: {
          description: 'Wyoming follows a modified Income Shares Model that estimates what parents would spend on their children if the family were intact, with adjustments for Wyoming-specific factors.',
          steps: [
            'Calculate each parent\'s gross monthly income from all verifiable sources',
            'Determine allowable deductions including taxes, mandatory retirement, and existing support orders',
            'Calculate net monthly income for each parent after allowable deductions',
            'Add both parents\' net incomes to determine combined monthly net income',
            'Use Wyoming Child Support Schedule to find basic support obligation for number of children',
            'Allocate support obligation proportionally based on each parent\'s percentage of combined income',
            'Add costs for health insurance, childcare, and extraordinary medical expenses',
            'Apply any adjustments for extended visitation time or special circumstances',
            'Calculate final monthly support amount payable by obligor parent'
          ]
        },
        incomeTypes: {
          included: [
            'Wages, salary, commissions, bonuses, and overtime pay',
            'Self-employment income (gross receipts minus ordinary business expenses)',
            'Investment income, dividends, interest, and capital gains',
            'Rental property income (after reasonable expenses)',
            'Retirement benefits, pensions, and annuity payments',
            'Social Security benefits (including disability and survivor benefits)',
            'Unemployment compensation and workers\' compensation benefits',
            'Military pay including base pay, allowances, and special pay',
            'Spousal support or maintenance received from previous relationships',
            'Trust income and regularly received gifts or inheritances',
            'Royalties, patents, and intellectual property income',
            'Business income from partnerships, corporations, or LLCs'
          ],
          excluded: [
            'Means-tested public assistance (TANF, SNAP, WIC, etc.)',
            'Foster care payments received for non-biological children',
            'Child support received for other children not subject to current order',
            'One-time gifts or inheritances (unless creating ongoing income)',
            'Student loans, grants, and educational financial aid',
            'Supplemental Security Income (SSI)',
            'Income of new spouse or cohabitating partner',
            'Reimbursement for job-related expenses',
            'Income from illegal activities'
          ]
        },
        deductions: {
          mandatory: [
            'Federal income taxes based on appropriate filing status',
            'State income taxes (Wyoming has no state income tax)',
            'FICA taxes (Social Security and Medicare)',
            'Mandatory retirement contributions required by employer',
            'Union dues required as condition of employment',
            'Court-ordered child support for other children',
            'Court-ordered spousal support/maintenance to former spouse'
          ],
          optional: [
            'Health insurance premiums for the child (added separately to support)',
            'Childcare expenses necessary for employment or education',
            'Extraordinary medical expenses for the child (shared proportionally)'
          ]
        },
        visitationAdjustment: {
          description: 'Wyoming allows adjustment to child support when the paying parent has significant overnight visitation time with the child.',
          thresholds: [
            'No adjustment for less than 25% overnight visitation (91 nights per year)',
            'Potential adjustment for 25-35% overnight visitation (91-127 nights per year)',
            'Standard adjustment consideration for 35%+ overnight visitation (128+ nights per year)',
            'Extended visitation adjustment may apply for equal or near-equal time arrangements'
          ],
          calculation: 'Adjustment reduces support obligation based on additional direct costs during extended visitation and reduced costs to custodial parent',
          factors: [
            'Actual overnight stays documented over previous 12 months',
            'Additional housing costs for accommodating child during visitation',
            'Duplicate expenses (clothing, toys, school supplies) in both homes',
            'Transportation costs for visitation exchanges',
            'Reduced household expenses for custodial parent during visitation periods'
          ]
        },
        additionalExpenses: {
          healthInsurance: {
            description: 'Cost of health insurance premiums specifically attributable to covering the child',
            allocation: 'Shared proportionally between parents based on their respective income percentages',
            requirements: 'Must be reasonable in cost, accessible to child, and provide adequate coverage',
            considerations: [
              'Premium cost must be specifically for child coverage, not family plan differential',
              'Insurance must be available through employer or affordable marketplace',
              'Coverage should include medical, dental, and vision if reasonably available',
              'Both parents should have access to insurance cards and provider information'
            ]
          },
          childcare: {
            description: 'Necessary childcare costs directly related to employment, job training, or education',
            allocation: 'Shared proportionally between parents based on their respective income percentages',
            requirements: 'Must be work-related or education-related necessity for custodial parent',
            considerations: [
              'Childcare must be necessary for parent to work or attend school',
              'Costs should be reasonable for the local area and type of care',
              'Before and after school care is typically included',
              'Summer childcare during school breaks may be included',
              'Receipts and documentation required for verification'
            ]
          },
          extraordinaryMedical: {
            description: 'Uninsured medical, dental, and vision expenses exceeding $100 per year per child',
            examples: [
              'Dental treatment not covered by insurance (fillings, crowns, extractions)',
              'Orthodontic treatment and appliances',
              'Vision care including eye exams, glasses, and contact lenses',
              'Mental health counseling and therapy sessions',
              'Physical therapy and rehabilitation services',
              'Prescription medications not covered by insurance',
              'Medical equipment and supplies (braces, wheelchairs, etc.)',
              'Treatment for chronic conditions requiring ongoing care',
              'Emergency medical treatment and hospitalization',
              'Specialized medical care and consultations'
            ],
            allocation: 'Shared proportionally between parents based on their respective income percentages',
            procedure: 'Parent incurring expense should seek pre-approval for non-emergency expenses over $500'
          },
          extracurricular: {
            description: 'Costs for children\'s activities, sports, and enrichment programs',
            examples: [
              'Youth sports team fees, equipment, and travel costs',
              'Music lessons and instrument rental or purchase',
              'Dance, martial arts, gymnastics, or other activity classes',
              'Summer camps and recreational day programs',
              'School trips, field trips, and educational activities',
              'Tutoring and educational support services',
              'Art, theater, or other creative programs',
              'Religious education and confirmation programs'
            ],
            note: 'Not automatically included in basic support calculation - must be agreed upon by parents or specifically court-ordered',
            considerations: [
              'Activities should be in the child\'s best interest and age-appropriate',
              'Costs should be reasonable and within family\'s financial means',
              'Both parents should have input on major extracurricular decisions',
              'Transportation to activities should be considered in planning'
            ]
          }
        },
        modifications: {
          grounds: [
            'Substantial change in either parent\'s income (typically 20% or more)',
            'Significant change in visitation schedule or custody arrangement',
            'Change in health insurance costs or availability',
            'Change in childcare expenses or needs',
            'Child\'s needs change significantly (special needs, medical conditions)',
            'Emancipation, marriage, or death of a child',
            'Three years have passed since last order (allows for cost-of-living review)',
            'Change in number of children covered by order'
          ],
          process: [
            'File Petition for Modification of Child Support with supporting documentation',
            'Serve the other parent with petition and financial affidavit',
            'Provide updated income verification and expense documentation',
            'Attend court hearing or mediation as scheduled by court',
            'Present evidence supporting the requested modification',
            'Obtain modified court order with new support amount'
          ],
          timeline: 'Modifications are typically effective from the date of filing the petition, not the date of the final court order',
          documentation: [
            'Updated financial affidavit with current income and expenses',
            'Recent pay stubs, tax returns, and employment verification',
            'Documentation of changed circumstances (medical bills, job loss, etc.)',
            'Current childcare and health insurance costs',
            'Evidence of visitation schedule changes if applicable'
          ]
        },
        enforcement: {
          methods: [
            'Income withholding and automatic wage garnishment',
            'Interception of tax refunds (federal and state)',
            'Asset seizure including bank accounts and personal property',
            'Liens on real estate and personal property',
            'Suspension of driver\'s license and professional licenses',
            'Suspension of hunting, fishing, and recreational licenses',
            'Credit bureau reporting of delinquent support',
            'Contempt of court proceedings with possible jail time',
            'Passport denial for arrearages exceeding $2,500',
            'Interception of lottery winnings and gaming proceeds',
            'Garnishment of unemployment and workers\' compensation benefits'
          ],
          interest: 'Unpaid child support accrues interest at the rate of 10% per annum on past-due amounts',
          statute: 'No statute of limitations exists for collecting past-due child support - it remains collectible indefinitely',
          resources: [
            'Wyoming Department of Family Services Child Support Enforcement',
            'Local prosecuting attorney offices for contempt proceedings',
            'Private collection agencies contracted by the state',
            'Interstate enforcement through other state agencies'
          ]
        },
        calculator: {
          worksheets: [
            'Wyoming Child Support Obligation Worksheet (basic calculation)',
            'Shared Physical Custody Worksheet (for significant visitation time)',
            'Multiple Family Worksheet (when parent has children with multiple partners)',
            'Self-Employment Income Worksheet (for business owners and contractors)'
          ],
          online: 'Wyoming Department of Family Services provides online calculator tools for estimation purposes',
          accuracy: 'Court calculations may differ from estimates based on specific case circumstances and judicial discretion',
          updates: 'Guidelines are reviewed every four years and updated as necessary to reflect economic changes'
        },
        specialCircumstances: {
          military: {
            description: 'Special considerations for military families stationed in or from Wyoming',
            factors: [
              'Military allowances (BAH, BAS, etc.) included in income calculation',
              'Deployment considerations for visitation schedules',
              'Geographic mobility challenges for maintaining relationships',
              'Military health insurance (TRICARE) considerations',
              'Family Servicemembers\' Group Life Insurance benefits'
            ]
          },
          selfEmployed: {
            description: 'Additional requirements for self-employed parents',
            documentation: [
              'Business tax returns for previous two years',
              'Profit and loss statements',
              'Business bank account records',
              'Accounts receivable and payable records',
              'Business expense documentation and receipts'
            ],
            considerations: [
              'Income averaging over multiple years may be used',
              'Personal expenses claimed as business deductions may be added back',
              'Depreciation and other non-cash deductions may be added to income',
              'Court may impute income based on earning capacity if income appears artificially reduced'
            ]
          },
          highIncome: {
            description: 'Considerations for families with combined income exceeding guideline tables',
            approach: [
              'Guidelines may be extrapolated beyond highest income level in tables',
              'Court may deviate from guidelines based on child\'s actual needs',
              'Additional expenses for private school, special activities may be considered',
              'Lifestyle and standard of living factors may influence decision'
            ]
          }
        }
      }
    },
    resources: {
      courts: [
        {
          name: 'Wyoming Judicial Branch Family Court Resources',
          url: 'https://www.courts.state.wy.us/family-court/',
          description: 'Official Wyoming court system family division with forms, procedures, and self-help resources'
        },
        {
          name: 'Wyoming Family Court Forms',
          url: 'https://www.courts.state.wy.us/legal-forms/family-law-forms/',
          description: 'Complete collection of Wyoming family court forms for divorce, custody, and support cases'
        },
        {
          name: 'Wyoming Self-Represented Litigant Resources',
          url: 'https://www.courts.state.wy.us/legal-help/',
          description: 'Self-help resources and assistance for people representing themselves in family court'
        },
        {
          name: 'First Judicial District Court (Cheyenne)',
          address: '309 West 20th Street, Cheyenne, WY 82001',
          phone: '(307) 633-4264',
          description: 'Family court serving Laramie County including Cheyenne area'
        },
        {
          name: 'Seventh Judicial District Court (Casper)',
          address: '200 North Center Street, Casper, WY 82601',
          phone: '(307) 235-9206',
          description: 'Family court serving Natrona County including Casper area'
        },
        {
          name: 'Third Judicial District Court (Jackson)',
          address: '180 South King Street, Jackson, WY 83001',
          phone: '(307) 733-4323',
          description: 'Family court serving Teton County including Jackson Hole area'
        }
      ],
      support: [
        {
          name: 'Wyoming Department of Family Services - Child Support',
          url: 'https://dfsweb.wyo.gov/child-support-services',
          phone: '1-800-970-3237',
          description: 'Official state agency for child support establishment, modification, and enforcement'
        },
        {
          name: 'Wyoming Child Support Payment Center',
          url: 'https://www.wyomingchildsupport.com/',
          description: 'Official payment processing and case management portal for Wyoming child support'
        },
        {
          name: 'Legal Aid of Wyoming',
          url: 'https://www.legalaidofwyoming.org/',
          phone: '1-877-432-9955',
          description: 'Free legal services for low-income families in family law matters statewide'
        },
        {
          name: 'Wyoming State Bar Lawyer Referral Service',
          url: 'https://www.wyomingbar.org/for-the-public/lawyer-referral-service/',
          phone: '(307) 632-9061',
          description: 'Professional attorney referrals for family law cases throughout Wyoming'
        },
        {
          name: 'Wyoming Coalition Against Domestic Violence',
          url: 'https://www.wyomingdvsa.org/',
          phone: '1-800-990-3877',
          description: 'Statewide domestic violence resources, shelters, and legal advocacy services'
        },
        {
          name: 'Wyoming Fathers Rights Support Groups',
          url: 'https://www.facebook.com/groups/wyomingfathersrights',
          description: 'Support networks and advocacy groups for fathers navigating family court in Wyoming'
        }
      ],
      legal: [
        {
          name: 'Wyoming State Bar Association Family Law Section',
          url: 'https://www.wyomingbar.org/sections/family-law/',
          description: 'Professional resources and directory of family law attorneys in Wyoming'
        },
        {
          name: 'University of Wyoming College of Law Legal Clinic',
          url: 'https://www.uwyo.edu/law/experiential-learning/clinics/',
          description: 'Student-supervised legal services for qualifying family law matters'
        },
        {
          name: 'Wyoming Collaborative Law Professionals',
          url: 'https://www.collaborativelaw.org/find-a-professional/wyoming/',
          description: 'Network of collaborative divorce attorneys and professionals in Wyoming'
        },
        {
          name: 'Wyoming Mediation Association',
          url: 'https://www.wyomingmediation.org/',
          description: 'Family mediation services and dispute resolution professionals statewide'
        },
        {
          name: 'Pro Bono Programs Wyoming',
          url: 'https://www.wyomingbar.org/for-the-public/pro-bono/',
          description: 'Free and reduced-cost legal services for qualifying individuals'
        }
      ],
      educational: [
        {
          name: 'Focus on Children Wyoming',
          url: 'https://www.courts.state.wy.us/family-court/parent-education/',
          description: 'Parent education program for divorcing parents with minor children (may be required)'
        },
        {
          name: 'Wyoming Parenting Plan Resources',
          url: 'https://www.courts.state.wy.us/family-court/parenting-plans/',
          description: 'Comprehensive guide to creating effective parenting plans in Wyoming'
        },
        {
          name: 'Wyoming Guardian ad Litem Program',
          url: 'https://www.courts.state.wy.us/family-court/guardian-ad-litem/',
          description: 'Information about guardian ad litem services for children in contested custody cases'
        },
        {
          name: 'University of Wyoming Extension - Family Life',
          url: 'https://www.uwyo.edu/uwe/programs/family/',
          description: 'Educational programs for co-parenting, family relationships, and child development'
        },
        {
          name: 'Wyoming Parent Information and Resource Centers',
          url: 'https://www.uwyo.edu/wind/programs/pirc/',
          description: 'Educational resources for parents navigating special needs and educational decisions'
        }
      ],
      filingInformation: {
        venues: [
          {
            name: 'First Judicial District Court',
            address: '309 West 20th Street, Cheyenne, WY 82001',
            phone: '(307) 633-4264',
            jurisdiction: 'Laramie County - Cheyenne, Pine Bluffs, Burns, and surrounding areas'
          },
          {
            name: 'Second Judicial District Court',
            address: '107 West 19th Avenue, Torrington, WY 82240',
            phone: '(307) 532-2147',
            jurisdiction: 'Goshen and Platte Counties - Torrington, Wheatland, Guernsey areas'
          },
          {
            name: 'Third Judicial District Court',
            address: '180 South King Street, Jackson, WY 83001',
            phone: '(307) 733-4323',
            jurisdiction: 'Lincoln, Sublette, and Teton Counties - Jackson, Kemmerer, Pinedale areas'
          },
          {
            name: 'Fourth Judicial District Court',
            address: '415 North 2nd Street, Laramie, WY 82072',
            phone: '(307) 721-2508',
            jurisdiction: 'Albany County - Laramie and surrounding areas'
          },
          {
            name: 'Fifth Judicial District Court',
            address: '2120 Capitol Avenue, Cheyenne, WY 82001',
            phone: '(307) 777-7480',
            jurisdiction: 'Carbon County - Rawlins, Saratoga, and surrounding areas'
          },
          {
            name: 'Sixth Judicial District Court',
            address: '721 Robertson Avenue, Thermopolis, WY 82443',
            phone: '(307) 864-3266',
            jurisdiction: 'Hot Springs and Park Counties - Thermopolis, Cody areas'
          },
          {
            name: 'Seventh Judicial District Court',
            address: '200 North Center Street, Casper, WY 82601',
            phone: '(307) 235-9206',
            jurisdiction: 'Natrona County - Casper and surrounding areas'
          },
          {
            name: 'Eighth Judicial District Court',
            address: '2 North Main Street, Lander, WY 82520',
            phone: '(307) 332-1080',
            jurisdiction: 'Fremont County - Lander, Riverton, and Wind River Reservation'
          },
          {
            name: 'Ninth Judicial District Court',
            address: '225 9th Street, Evanston, WY 82930',
            phone: '(307) 783-0307',
            jurisdiction: 'Uinta County - Evanston, Mountain View areas'
          }
        ],
        filingFees: {
          'Divorce (with children)': '$70.00',
          'Divorce (without children)': '$70.00',
          'Custody/Visitation': '$70.00',
          'Child Support': '$70.00',
          'Modification': '$70.00',
          'Paternity': '$70.00',
          'Protection Order': 'No fee'
        },
        requiredForms: [
          'Petition for Divorce or Custody',
          'Summons',
          'Financial Affidavit',
          'Parenting Plan (if children involved)',
          'Child Support Worksheets',
          'Certificate of Service',
          'Decree of Divorce or Custody Order'
        ]
      },
      statutes: [
        {
          name: 'Wyoming Statutes Title 20 - Domestic Relations',
          url: 'https://www.wyoleg.gov/statutes/compress/title20.pdf',
          description: 'Primary statutes governing divorce, custody, and family law in Wyoming'
        },
        {
          name: 'Wyoming Child Support Guidelines (W.S. § 20-2-304)',
          url: 'https://www.wyoleg.gov/statutes/compress/title20.pdf',
          description: 'Official child support calculation guidelines and procedures'
        },
        {
          name: 'Wyoming Uniform Child Custody Jurisdiction Act (W.S. § 20-5-101 et seq.)',
          url: 'https://www.wyoleg.gov/statutes/compress/title20.pdf',
          description: 'Interstate custody jurisdiction and enforcement laws'
        },
        {
          name: 'Wyoming Parental Rights and Responsibilities (W.S. § 20-2-201)',
          url: 'https://www.wyoleg.gov/statutes/compress/title20.pdf',
          description: 'Custody determination factors and best interests standards'
        }
      ],
      emergencyContacts: [
        {
          name: 'Wyoming Domestic Violence Hotline',
          phone: '1-800-990-3877',
          description: '24/7 statewide domestic violence crisis line and support services'
        },
        {
          name: 'Wyoming Child Abuse Hotline',
          phone: '1-800-457-3659',
          description: '24/7 child abuse and neglect reporting hotline'
        },
        {
          name: 'Wyoming Crisis Intervention',
          phone: '211',
          description: 'Dial 211 for mental health crisis intervention and counseling referrals'
        },
        {
          name: 'Legal Aid of Wyoming Emergency Line',
          phone: '1-877-432-9955',
          description: 'Emergency legal assistance hotline for urgent family law matters'
        }
      ]
    },
    bestInterestsFactors: [
      'The quality of the relationship each parent has with the child and the ability of each parent to provide love, affection and guidance',
      'The competency and fitness of each parent',
      'The willingness and ability of each parent to facilitate and encourage a close and continuing relationship between the other parent and child',
      'How the child has been cared for, by whom the child has been cared for, and who has made the decisions regarding the child\'s upbringing',
      'The geographical distance between the parents\' residences',
      'The current physical and mental ability of each parent to care for the child',
      'Which parent is more likely to allow the child frequent, meaningful and continuing contact with the other parent',
      'The wishes of the child if the child is of sufficient age and capacity to form a rational preference',
      'Whether either parent is subject to any criminal conviction',
      'Whether either parent has been subject to any domestic violence',
      'The parental capacity to provide physical care, emotional stability and nurturing guidance for the child',
      'The previous relationship of the child with each parent'
    ],
    practicalTips: [
      'Wyoming courts value stability and continuity - document your consistent involvement in your child\'s life',
      'Keep detailed records of all visitation time, communications with the other parent, and expenses',
      'Be prepared for potential long-distance travel due to Wyoming\'s rural nature and limited court locations',
      'Weather conditions can significantly impact visitation - include flexible provisions in your parenting plan',
      'Wyoming courts appreciate parents who can demonstrate cooperation and communication abilities',
      'Consider hiring a local attorney familiar with Wyoming judges and court procedures',
      'Take advantage of extended summer visitation opportunities common in Wyoming custody arrangements',
      'Document any special needs or circumstances that might affect custody or support calculations'
    ]
  }
};

export default wyomingData;